import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import type { Item, Location, Requisition, PurchaseOrder, Vendor } from '@/lib/schemas';

interface SearchResults {
  items: Item[];
  locations: Location[];
  requisitions: Requisition[];
  purchaseOrders: PurchaseOrder[];
  vendors: Vendor[];
}

export function useGlobalSearch(query: string) {
  const { user } = useAuth();
  const debounceRef = useRef<NodeJS.Timeout>();
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const [isOpen, setIsOpen] = useState(false);

  // Debounce search input
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (query.trim()) {
      debounceRef.current = setTimeout(() => {
        setDebouncedQuery(query);
      }, 300);
    } else {
      setDebouncedQuery('');
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  const { data, isLoading, error } = useQuery<SearchResults, Error>({
    queryKey: ['global-search', debouncedQuery],
    queryFn: async () => {
      if (!debouncedQuery) {
        return {
          items: [],
          locations: [],
          requisitions: [],
          purchaseOrders: [],
          vendors: []
        };
      }
      return api.globalSearch(debouncedQuery);
    },
    staleTime: 0,
    enabled: !!debouncedQuery,
  });

  const hasResults = data && (
    (data.items?.length || 0) +
    (data.locations?.length || 0) +
    (data.requisitions?.length || 0) +
    (data.purchaseOrders?.length || 0) +
    (data.vendors?.length || 0) > 0
  );

  return {
    results: data || { items: [], locations: [], requisitions: [], purchaseOrders: [], vendors: [] },
    isLoading,
    error,
    hasResults,
    isOpen,
    setIsOpen,
  };
}
